import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getReportUserDetails,
  getReportCompanyCode,
  isReportAdmin,
  loadReportBranches,
  getUniqueCompaniesFromBranches,
  filterBranchesByCompany,
  getDefaultAdminCompanyCode,
  resolveEffectiveCompanyCode,
  getStoredAdminCompanyCode,
  setStoredAdminCompanyCode,
  ALL_COMPANIES_TOKEN,
} from '../helpers/reportBranches';

export const useReportScope = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('x-access-token');
  const userDetails = useMemo(() => getReportUserDetails(), []);
  const sessionCompanyCode = getReportCompanyCode(userDetails);
  const admin = isReportAdmin(userDetails);
  const needsCompanyPicker = admin && !sessionCompanyCode;

  const [allBranches, setAllBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [adminCompanyCode, setAdminCompanyCodeState] = useState(() => getStoredAdminCompanyCode());

  const setAdminCompanyCode = useCallback((companyCode) => {
    const trimmed = String(companyCode || '').trim();
    setAdminCompanyCodeState(trimmed || null);
    setStoredAdminCompanyCode(trimmed || null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!token) {
        setAllBranches([]);
        return;
      }

      if (!admin && !sessionCompanyCode) {
        setAllBranches([]);
        return;
      }

      setBranchesLoading(true);
      try {
        const branchList = await loadReportBranches(token, userDetails);
        if (cancelled) {
          return;
        }

        setAllBranches(branchList);

        if (needsCompanyPicker) {
          const stored = getStoredAdminCompanyCode();
          const defaultCode = getDefaultAdminCompanyCode(branchList);
          const storedIsValid = stored && (
            stored === ALL_COMPANIES_TOKEN ||
            branchList.some((branch) => branch.SirketKodu === stored)
          );
          if (!storedIsValid && defaultCode) {
            setAdminCompanyCode(defaultCode);
          } else if (!stored && defaultCode) {
            setAdminCompanyCode(defaultCode);
          }
        }
      } catch {
        if (!cancelled) {
          setAllBranches([]);
        }
      } finally {
        if (!cancelled) {
          setBranchesLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token, admin, sessionCompanyCode, needsCompanyPicker, userDetails, setAdminCompanyCode]);

  const companies = useMemo(() => {
    if (!needsCompanyPicker) {
      return [];
    }
    const unique = getUniqueCompaniesFromBranches(allBranches);
    if (unique.length > 1) {
      return [
        { SirketKodu: ALL_COMPANIES_TOKEN, SirketAdi: 'Tüm Şirketler' },
        ...unique,
      ];
    }
    return unique;
  }, [needsCompanyPicker, allBranches]);

  const effectiveCompanyCode = useMemo(
    () => resolveEffectiveCompanyCode(sessionCompanyCode, null, allBranches, adminCompanyCode),
    [sessionCompanyCode, allBranches, adminCompanyCode]
  );

  const branches = useMemo(
    () => filterBranchesByCompany(allBranches, effectiveCompanyCode),
    [allBranches, effectiveCompanyCode]
  );

  const getCompanyCodeForRequest = useCallback(
    (branchCode = null, adminCompanyOverride = undefined) => resolveEffectiveCompanyCode(
      sessionCompanyCode,
      branchCode,
      allBranches,
      adminCompanyOverride !== undefined ? adminCompanyOverride : adminCompanyCode
    ),
    [sessionCompanyCode, allBranches, adminCompanyCode]
  );

  return {
    token,
    userDetails,
    sessionCompanyCode,
    isAdmin: admin,
    needsCompanyPicker,
    companies,
    adminCompanyCode,
    setAdminCompanyCode,
    allBranches,
    branches,
    branchesLoading,
    effectiveCompanyCode,
    canFetchReports: Boolean(effectiveCompanyCode),
    getCompanyCodeForRequest,
  };
};
